/* eslint-disable react/prop-types */
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import RevaluationRecordTable from "./info";
import { FixedAssetRevaluationService } from "services/fixed-asset-revaluation";
import { useSelector } from "react-redux";
import { getAuth } from "redux/selector";
import { getPrintTemplate } from "../components/printTemplate";

const initialValues = {
  unit: "",
  department: "",
  documentNumber: "",
  debitAccount: "",
  creditAccount: "",
  revDay: "",
  revMonth: "",
  revYear: "",
  decisionNumber: "",
  decisionDay: "",
  decisionMonth: "",
  decisionYear: "",
  decisionMaker: "",
  committee: [
    { name: "", position: "", representative: "", role: "Chủ tịch Hội đồng" },
    { name: "", position: "", representative: "", role: "Ủy viên" },
    { name: "", position: "", representative: "", role: "Ủy viên" },
  ],
  conclusion: "",
  creatorName: "",
  accountantName: "",
  headOfCommitteeName: "",
  signingDate: "",
  items: [],
  total: null,
};

function FixedAssetRevaluationDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containEdit = location.pathname?.includes("/edit");
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [getData, setGetData] = useState({});
  const [data, setData] = useState({});

  const auth = useSelector(getAuth);
  const [formInitials, setFormInitials] = useState(initialValues);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await FixedAssetRevaluationService.getById(id);
        const apiData = response.data ? response.data : response;

        // Map API data to flat form (nested → flat)
        const mappedItems = (apiData.items || []).map((item) => ({
          orderNumber: item.orderNumber || "",
          assetNameAndSpecs: item.assetNameAndSpecs || "",
          assetCode: item.assetCode || "",
          assetCardNumber: item.assetCardNumber || "",
          bookOriginalCost: item.bookValue?.originalCost || item.bookValue?.remainingValue || "",
          bookDepreciation: item.bookValue?.depreciation || "",
          bookRemainingValue: item.bookValue?.remainingValue || "",
          revOriginalCost: item.revaluedValue?.originalCost || "",
          revDepreciation: item.revaluedValue?.depreciation || "",
          revRemainingValue: item.revaluedValue?.remainingValue || "",
          diffOriginalCost: item.variance?.originalCost || "",
          diffDepreciation: item.variance?.depreciation || "",
          diffRemainingValue: item.variance?.remainingValue || "",
        }));

        const mappedTotal = {
          bookOriginalCost: apiData.totals?.bookValueTotal?.originalCost || "",
          bookDepreciation: apiData.totals?.bookValueTotal?.depreciation || "",
          bookRemainingValue: apiData.totals?.bookValueTotal?.remainingValue || "",
          revOriginalCost: apiData.totals?.revaluedValueTotal?.originalCost || "",
          revDepreciation: apiData.totals?.revaluedValueTotal?.depreciation || "",
          revRemainingValue: apiData.totals?.revaluedValueTotal?.remainingValue || "",
          diffOriginalCost: apiData.totals?.varianceTotal?.originalCost || "",
          diffDepreciation: apiData.totals?.varianceTotal?.depreciation || "",
          diffRemainingValue: apiData.totals?.varianceTotal?.remainingValue || "",
        };

        const loadedData = {
          ...initialValues,
          unit: apiData.company?.name || apiData.unit || "",
          department: apiData.department || "",
          documentNumber: apiData.recordNumber || apiData.documentNumber || "",
          debitAccount: apiData.debitAccount || "",
          creditAccount: apiData.creditAccount || "",
          // Parse revaluation date if available
          decisionNumber: apiData.decisionNumber || "",
          decisionDate: apiData.decisionDate,
          decisionDay: apiData.decisionDate
            ? new Date(apiData.decisionDate).getDate().toString()
            : "",
          decisionMonth: apiData.decisionDate
            ? (new Date(apiData.decisionDate).getMonth() + 1).toString()
            : "",
          decisionYear: apiData.decisionDate
            ? new Date(apiData.decisionDate).getFullYear().toString()
            : "",
          revaluationDate: apiData.revaluationDate,
          revDay: apiData.revaluationDate
            ? new Date(apiData.revaluationDate).getDate().toString()
            : "",
          revMonth: apiData.revaluationDate
            ? (new Date(apiData.revaluationDate).getMonth() + 1).toString()
            : "",
          revYear: apiData.revaluationDate
            ? new Date(apiData.revaluationDate).getFullYear().toString()
            : "",
          decisionMaker: apiData.decisionMaker || "",
          committee: apiData.revaluationCommittee || apiData.committee || initialValues.committee,
          conclusion: apiData.conclusion || "",
          signingDate: apiData.signingDate || new Date().toISOString().split("T")[0],
          creatorName: apiData.creatorName || "",
          accountantName: apiData.accountantName || "",
          headOfCommitteeName: apiData.headOfCommitteeName || "",
          items: mappedItems.length > 0 ? mappedItems : initialValues.items,
          total: mappedTotal,
        };

        setFormInitials(loadedData);
        setGetData(apiData);
      } catch (error) {
        console.error("Lỗi getById", error);
        toast.error("Không thể tải dữ liệu");
      }
    })();
  }, [id]);

  const onChangeValue = useCallback((type, value) => {
    setData((prev) => ({ ...prev, [type]: value }));
  }, []);

  const handleBack = () => navigation(-1);

  const submitForm = async (values) => {
    try {
      const num = (val) => (val && !isNaN(val) ? Number(val) : 0);

      // Use Formik values directly (source of truth) for scalars, data for complex objects
      const unit = values.unit || "";
      const department = values.department || "";
      const documentNumber =
        values.documentNumber ||
        `DGL-${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`;
      const debitAccount = values.debitAccount || "";
      const creditAccount = values.creditAccount || "";
      const committee = data.committee || values.committee || [];
      const signingDate = values.signingDate || new Date().toISOString().split("T")[0];
      const creatorName = values.creatorName || "";
      const accountantName = values.accountantName || "";
      const headOfCommitteeName = values.headOfCommitteeName || "";
      const conclusion = values.conclusion || "";
      const itemsFlat = data.items || values.items || [];
      const totalFlat = data.total || values.total || {};

      // Build revaluationDate from split fields
      const revaluationDate =
        values.revYear && values.revMonth && values.revDay
          ? `${values.revYear}-${String(values.revMonth).padStart(2, "0")}-${String(
              values.revDay
            ).padStart(2, "0")}`
          : new Date().toISOString().split("T")[0];

      // Build decisionDate from split fields
      const decisionDate =
        values.decisionYear && values.decisionMonth && values.decisionDay
          ? `${values.decisionYear}-${String(values.decisionMonth).padStart(2, "0")}-${String(
              values.decisionDay
            ).padStart(2, "0")}`
          : "";

      // Build nested backend structure from flat UI
      const buildNestedItem = (item) => ({
        orderNumber: item.orderNumber || "",
        assetNameAndSpecs: item.assetNameAndSpecs || "",
        assetCode: item.assetCode || "",
        assetCardNumber: item.assetCardNumber || "",
        bookValue: {
          originalCost: num(item.bookOriginalCost),
          depreciation: num(item.bookDepreciation),
          remainingValue: num(item.bookRemainingValue),
        },
        revaluedValue: {
          originalCost: num(item.revOriginalCost),
          depreciation: num(item.revDepreciation),
          remainingValue: num(item.revRemainingValue),
        },
        variance: {
          originalCost: num(item.diffOriginalCost),
          depreciation: num(item.diffDepreciation),
          remainingValue: num(item.diffRemainingValue),
        },
      });

      const buildItems = (itemsList) => itemsList.map(buildNestedItem);

      const buildTotals = () => ({
        bookValueTotal: {
          originalCost: num(totalFlat.bookOriginalCost),
          depreciation: num(totalFlat.bookDepreciation),
          remainingValue: num(totalFlat.bookRemainingValue),
        },
        revaluedValueTotal: {
          originalCost: num(totalFlat.revOriginalCost),
          depreciation: num(totalFlat.revDepreciation),
          remainingValue: num(totalFlat.revRemainingValue),
        },
        varianceTotal: {
          originalCost: num(totalFlat.diffOriginalCost),
          depreciation: num(totalFlat.diffDepreciation),
          remainingValue: num(totalFlat.diffRemainingValue),
        },
      });

      const payload = {
        company: auth?.user?.company?._id || "",
        unit,
        department,
        documentNumber,
        revaluationDate,
        recordNumber: documentNumber,
        debitAccount,
        creditAccount,
        decisionNumber: values.decisionNumber || "",
        decisionDate,
        decisionMaker: values.decisionMaker || "",
        revaluationCommittee: committee,
        items: buildItems(itemsFlat),
        totals: buildTotals(),
        conclusion,
        notes: values.notes || "",
        creatorName,
        accountantName,
        headOfCommitteeName,
        signingDate,
        status: id ? "DRAFT" : "DRAFT",
      };

      // Log for verification
      console.log("FixedAssetRevaluation Payload:", JSON.stringify(payload, null, 2));

      console.log("Backend payload:", JSON.stringify(payload, null, 2));

      console.log("Revaluation submit payload:", JSON.stringify(payload, null, 2));

      if (!id) {
        await FixedAssetRevaluationService.create(payload);
        toast.success("Tạo biên bản đánh giá lại TSCĐ thành công!");
      } else {
        await FixedAssetRevaluationService.update(id, payload);
        toast.success("Cập nhật biên bản đánh giá lại TSCĐ thành công!");
      }
    } catch (error) {
      console.error("Lỗi khi gọi API lưu:", error);
      toast.error(error?.response?.data?.message || "Lưu thất bại!");
      throw error;
    }
  };

  const handlePrintDirect = (values) => {
    const finalData = { ...values };
    if (data.items) finalData.items = data.items;
    if (data.total) finalData.total = data.total;
    if (data.committee) finalData.committee = data.committee;

    const headerInfo = {
      unit: finalData.unit,
      department: finalData.department,
      documentNumber: finalData.documentNumber,
      debitAccount: finalData.debitAccount,
      creditAccount: finalData.creditAccount,
      revDay: finalData.revDay,
      revMonth: finalData.revMonth,
      revYear: finalData.revYear,
      decisionNumber: finalData.decisionNumber,
      decisionDay: finalData.decisionDay,
      decisionMonth: finalData.decisionMonth,
      decisionYear: finalData.decisionYear,
      decisionMaker: finalData.decisionMaker,
      committee: finalData.committee,
      conclusion: finalData.conclusion,
      signingDate: finalData.signingDate,
    };

    const rows = finalData.items.map((item) => ({
      orderNumber: item.orderNumber,
      assetNameAndSpecs: item.assetNameAndSpecs,
      assetCode: item.assetCode,
      assetCardNumber: item.assetCardNumber,
      bookOriginalCost: item.bookOriginalCost,
      bookDepreciation: item.bookDepreciation,
      bookRemainingValue: item.bookRemainingValue,
      revOriginalCost: item.revOriginalCost,
      revDepreciation: item.revDepreciation,
      revRemainingValue: item.revRemainingValue,
      diffOriginalCost: item.diffOriginalCost,
      diffDepreciation: item.diffDepreciation,
      diffRemainingValue: item.diffRemainingValue,
    }));

    const totals = finalData.total || {};

    const htmlContent = getPrintTemplate({
      headerInfo,
      rows,
      totals,
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleSubmit = async (values, actions) => {
    try {
      await submitForm(values);
    } catch (error) {
      // Error handled in submitForm
    } finally {
      if (actions) {
        actions.setTouched({});
        actions.setSubmitting(false);
      }
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center" sx={{ height: "100%" }}>
          <Grid item xs={12} lg={12}>
            <Formik initialValues={formInitials} enableReinitialize={true} onSubmit={handleSubmit}>
              {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                <Form autoComplete="off">
                  <Card sx={{ height: "100%" }}>
                    <SoftBox p={2}>
                      <SoftBox>
                        <RevaluationRecordTable
                          formData={{
                            values,
                            touched,
                            errors,
                            getData,
                            onChangeValue,
                            setFieldValue,
                          }}
                        />
                        <SoftBox mt={10} width="100%" display="flex" justifyContent="space-between">
                          <SoftButton variant="gradient" color="light" onClick={handleBack}>
                            Trở về
                          </SoftButton>

                          {containView ? (
                            <SoftBox display="flex" gap={3}>
                              <SoftButton
                                variant="gradient"
                                color="success"
                                onClick={() => handlePrintDirect(values)}
                                type="button"
                              >
                                In Biên Bản Đánh Giá Lại
                              </SoftButton>
                            </SoftBox>
                          ) : (
                            <SoftBox display="flex" gap={3}>
                              <SoftButton
                                disabled={isSubmitting}
                                type="submit"
                                variant="gradient"
                                color="dark"
                              >
                                {id ? "Lưu" : "Tạo"}
                              </SoftButton>
                            </SoftBox>
                          )}
                        </SoftBox>
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default FixedAssetRevaluationDetail;
